import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { 
  MapPin, 
  Mountain, 
  Home,
  Mail,
  Phone,
  MessageCircle,
  Instagram,
  Facebook,
  HelpCircle,
  Mic,
  Users
} from "lucide-react";

import JsonLd from "../components/JsonLd";
import { pic } from "../lib/pic";

const ContactPage = () => {
  const contactSchemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "url": "https://www.savicoliving.com/contact",
    "mainEntity": {
      "@type": "LodgingBusiness",
      "name": "Savi Coliving",
      "url": "https://www.savicoliving.com",
      "telephone": "+34632628051",
      "email": "hola@savicoliving.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Náquera",
        "addressRegion": "Valencia",
        "addressCountry": "ES"
      },
      "contactPoint": [{
        "@type": "ContactPoint",
        "telephone": "+34632628051",
        "contactType": "reservations",
        "availableLanguage": ["English", "Spanish"],
        "areaServed": "Worldwide"
      }],
      "sameAs": [
        "https://www.facebook.com/savicoliving",
        "https://www.instagram.com/savicoliving"
      ]
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Contact Steve | Savi Coliving Valencia</title>
        <meta name="description" content="Apply to stay at Savi Coliving. WhatsApp +34 632 62 80 51 or email hola@savicoliving.com. Naquera, Valencia, Spain." />
        <meta property="og:title" content="Contact Savi Coliving" />
        <meta property="og:description" content="Reach out to book your room or ask questions. WhatsApp, email, or visit us in Náquera, Valencia." />
        <meta name="twitter:card" content="summary" />
        <link rel="canonical" href="https://www.savicoliving.com/contact" />
      </Helmet>
      <JsonLd id="contact" data={contactSchemaData} />
      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <img 
            {...pic("mountain-view")}
            fetchpriority="high" decoding="async"
            alt="Contact Savi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-15% via-[#FBF7F1]/75 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">Contact Us</h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base">See you in the mountains.</p>
            </div>
          </div>
        </div>
      </header>

      {/* Contact Content */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">Get in Touch</h2>
            <p className="text-[#2E2A26]/60 text-sm sm:text-base max-w-xl mx-auto">
              We'd love to hear from you. Reach out through any of these channels.
            </p>
          </div>
          
          {/* 2x2 Grid of Contact Methods */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {/* WhatsApp */}
            <a 
              href="https://wa.me/34632628051?text=Hi%20Steve!%20I'm%20interested%20in%20Savi%20Coliving." 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 sm:p-6 bg-[#25D366]/10 rounded-2xl hover:bg-[#25D366]/20 transition-colors group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-[#2E2A26] mb-1 text-base sm:text-lg">WhatsApp</h3>
                <p className="text-[#2E2A26]/60 text-sm">+34 632 628 051</p>
                <span className="text-[#25D366] text-xs font-medium group-hover:underline">Fastest response →</span>
              </div>
            </a>

            {/* Email */}
            <a 
              href="mailto:hola@savicoliving.com"
              className="flex items-center gap-4 p-5 sm:p-6 bg-[#C85A38]/10 rounded-2xl hover:bg-[#C85A38]/20 transition-colors group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#C85A38] flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-[#2E2A26] mb-1 text-base sm:text-lg">Email</h3>
                <p className="text-[#2E2A26]/60 text-sm">hola@savicoliving.com</p>
                <span className="text-[#C85A38] text-xs font-medium group-hover:underline">Send us a message →</span>
              </div>
            </a>

            {/* Phone */}
            <a 
              href="tel:+34632628051"
              className="flex items-center gap-4 p-5 sm:p-6 bg-[#2E2A26]/5 rounded-2xl hover:bg-[#2E2A26]/10 transition-colors group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#2E2A26] flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-[#2E2A26] mb-1 text-base sm:text-lg">Phone</h3>
                <p className="text-[#2E2A26]/60 text-sm">+34 632 628 051</p>
                <span className="text-[#2E2A26]/60 text-xs font-medium group-hover:underline">Give us a call →</span>
              </div>
            </a>

            {/* Location */}
            <a 
              href="https://maps.google.com/?q=Savi+Coliving+Valencia,Nàquera,Spain"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 sm:p-6 bg-[#8C9A7C]/10 rounded-2xl hover:bg-[#8C9A7C]/20 transition-colors group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#8C9A7C] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-[#2E2A26] mb-1 text-base sm:text-lg">Location</h3>
                <p className="text-[#2E2A26]/60 text-sm">Náquera, Valencia, Spain</p>
                <span className="text-[#8C9A7C] text-xs font-medium group-hover:underline">View on map →</span>
              </div>
            </a>
          </div>

          {/* Social Links */}
          <div className="mt-10 sm:mt-12 text-center">
            <p className="text-[#2E2A26]/60 text-sm mb-4">Follow us on social media</p>
            <div className="flex justify-center gap-4">
              <a 
                href="https://www.instagram.com/savicoliving/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center hover:scale-110 transition-transform"
              >
                <Instagram className="w-6 h-6 text-white" />
              </a>
              <a 
                href="https://www.facebook.com/people/Savi-Coliving/61577166869338/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform"
              >
                <Facebook className="w-6 h-6 text-white" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8 bg-[#F4ECE2]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-6 sm:mb-8">Find Us</h2>
          <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg h-[300px] sm:h-[400px]">
            <iframe 
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Savi+Coliving+Valencia,Nàquera,Spain&zoom=14"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Savi Coliving Location - Náquera, Valencia"
            ></iframe>
          </div>
          <p className="text-center text-[#2E2A26]/60 text-xs sm:text-sm mt-4">
            Partida Fontanelles 2, 61 • 46119 Nàquera, Valencia • 25 min from city center
          </p>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default ContactPage;
